import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Solution } from '@/types/evaluation.types';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Target, Plus, Pencil, Trash2, MoreVertical } from 'lucide-react';

interface CompetitiveSolutionsDisplayProps {
  solutions: Solution[];
  onAdd: (solution: Omit<Solution, 'id'>) => void;
  onEdit: (id: string, solution: Partial<Solution>) => void;
  onRemove: (id: string) => void;
}

/**
 * Display of solutions being evaluated
 */
export function CompetitiveSolutionsDisplay({
  solutions,
  onAdd,
  onEdit,
  onRemove,
}: CompetitiveSolutionsDisplayProps) {
  const { t } = useTranslation(['dashboard', 'common']);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingSolution, setEditingSolution] = useState<Solution | null>(null);
  const [formData, setFormData] = useState({ solutionName: '', vendorName: '' });

  const handleAddSolution = () => {
    if (formData.solutionName.trim()) {
      onAdd({
        solutionName: formData.solutionName.trim(),
        vendorName: formData.vendorName.trim() || undefined,
      });
      setFormData({ solutionName: '', vendorName: '' });
      setIsAddDialogOpen(false);
    }
  };

  const handleEditSolution = () => {
    if (editingSolution && formData.solutionName.trim()) {
      onEdit(editingSolution.id, {
        solutionName: formData.solutionName.trim(),
        vendorName: formData.vendorName.trim() || undefined,
      });
      setFormData({ solutionName: '', vendorName: '' });
      setEditingSolution(null);
      setIsEditDialogOpen(false);
    }
  };

  const openEditDialog = (solution: Solution) => {
    setEditingSolution(solution);
    setFormData({
      solutionName: solution.solutionName,
      vendorName: solution.vendorName || '',
    });
    setIsEditDialogOpen(true);
  };

  const closeEditDialog = () => {
    setEditingSolution(null);
    setFormData({ solutionName: '', vendorName: '' });
    setIsEditDialogOpen(false);
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Target className="h-5 w-5" aria-hidden="true" />
            {t('dashboard:solutions.title')} ({solutions.length})
          </h2>
          <Button onClick={() => setIsAddDialogOpen(true)} size="sm">
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            {t('dashboard:solutions.addButton')}
          </Button>
        </div>

        {solutions.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Target className="h-12 w-12 mx-auto mb-4 opacity-20" aria-hidden="true" />
            <p>{t('dashboard:solutions.empty')}</p>
            <p className="text-sm mt-2">{t('dashboard:solutions.emptyHint')}</p>
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {solutions.map((solution) => (
                <Card key={solution.id} className="relative overflow-hidden hover:border-primary/50 transition-colors">
                  <div className="absolute top-2 right-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          aria-label={t('common:actions.manage')}
                        >
                          <MoreVertical className="h-3.5 w-3.5" aria-hidden="true" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditDialog(solution)}>
                          <Pencil className="mr-2 h-4 w-4" aria-hidden="true" />
                          {t('common:actions.edit')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => onRemove(solution.id)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
                          {t('common:actions.delete')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <CardContent className="pt-4 pb-3 pr-10">
                    <div className="space-y-0.5">
                      <h3 className="font-semibold text-base leading-tight">
                        {solution.solutionName}
                      </h3>
                      {solution.vendorName && (
                        <p className="text-xs text-muted-foreground">
                          {solution.vendorName}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
          </div>
        )}
      </div>

      {/* Add Solution Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('dashboard:solutions.addDialog.title')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="solution-name">{t('dashboard:solutions.addDialog.solutionName')}</Label>
              <Input
                id="solution-name"
                value={formData.solutionName}
                onChange={(e) => setFormData({ ...formData, solutionName: e.target.value })}
                placeholder={t('dashboard:solutions.addDialog.solutionNamePlaceholder')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="vendor-name">{t('dashboard:solutions.addDialog.vendorName')}</Label>
              <Input
                id="vendor-name"
                value={formData.vendorName}
                onChange={(e) => setFormData({ ...formData, vendorName: e.target.value })}
                placeholder={t('dashboard:solutions.addDialog.vendorNamePlaceholder')}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              {t('common:actions.cancel')}
            </Button>
            <Button onClick={handleAddSolution} disabled={!formData.solutionName.trim()}>
              {t('common:actions.add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Solution Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={closeEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('dashboard:solutions.editDialog.title')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-solution-name">{t('dashboard:solutions.editDialog.solutionName')}</Label>
              <Input
                id="edit-solution-name"
                value={formData.solutionName}
                onChange={(e) => setFormData({ ...formData, solutionName: e.target.value })}
                placeholder={t('dashboard:solutions.editDialog.solutionNamePlaceholder')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-vendor-name">{t('dashboard:solutions.editDialog.vendorName')}</Label>
              <Input
                id="edit-vendor-name"
                value={formData.vendorName}
                onChange={(e) => setFormData({ ...formData, vendorName: e.target.value })}
                placeholder={t('dashboard:solutions.editDialog.vendorNamePlaceholder')}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeEditDialog}>
              {t('common:actions.cancel')}
            </Button>
            <Button onClick={handleEditSolution} disabled={!formData.solutionName.trim()}>
              {t('common:actions.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
